import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastType } from 'react-toastify';

import type { BlackbaudRaisersEdgeIntegration, IBlackbaudFund } from '@feathr/blackbox';
import { Button, Modal, toast } from '@feathr/components';

import BBFundSelect from './BBFundSelect';

interface IProps {
  integration: BlackbaudRaisersEdgeIntegration;
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function EnableDonationSyncModal({
  integration,
  opened,
  onClose,
  onSuccess,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [selectedFund, setSelectedFund] = useState<IBlackbaudFund | undefined>();

  useEffect(() => {
    if (!opened) {
      setSelectedFund(undefined);
    }
  }, [opened]);

  function handleFundChange(fund: IBlackbaudFund | undefined): void {
    setSelectedFund(fund);
  }

  async function handleConfirm(): Promise<void> {
    if (!selectedFund) {
      return;
    }

    try {
      integration.set({
        upsync_activities: {
          ...integration.get('upsync_activities'),
          donations: true,
        },
        fundraising: {
          fund_id: selectedFund.id,
        },
      });
      await integration.patchDirty();
      toast(
        t(`"{{- field}}" {{action}}`, {
          field: t("Upsync Donations to Raiser's Edge NXT"),
          action: t('enabled'),
        }),
        {
          type: ToastType.SUCCESS,
        },
      );
      onSuccess();
    } catch (error) {
      toast(t('Something went wrong while updating the setting:\n{{- error}}', { error }), {
        type: ToastType.ERROR,
      });
    }
  }

  const rightActions = [
    <Button key={'cancel'} onClick={onClose}>
      {t('Cancel')}
    </Button>,
    <Button disabled={!selectedFund} key={'confirm'} onClick={handleConfirm} type={'primary'}>
      {t('Confirm')}
    </Button>,
  ];

  return (
    <Modal
      allowOverflow
      description={t(
        'Funds represent how donors expect you to use their gifts. Donations earned through Feathr will be assigned to the selected fund.',
      )}
      onClose={onClose}
      opened={opened}
      rightActions={rightActions}
      title={t('Select a fund')}
    >
      <BBFundSelect
        integration={integration}
        label={t('Fund')}
        onChange={handleFundChange}
        value={selectedFund?.id}
      />
    </Modal>
  );
}

export default observer(EnableDonationSyncModal);
